import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Phone, MessageSquareWarning } from "lucide-react";

export default function SettingsIssuesDialog({ alerts, onFixPhone }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false);
  const phoneMissing = alerts?.settings?.count > 0;
  const failed = alerts?.sms?.count || 0;

  useEffect(() => {
    if (!shown && (phoneMissing || failed)) { setOpen(true); setShown(true); }
  }, [phoneMissing, failed, shown]);

  const issues = [
    phoneMissing && {
      id: "phone", icon: Phone, title: "Add your business phone number",
      desc: "It's shown in client texts so they know who to call, and receives job-completed alerts.",
      action: "Add phone number", run: () => { setOpen(false); onFixPhone(); },
    },
    failed > 0 && {
      id: "sms", icon: MessageSquareWarning, title: `${failed} text${failed === 1 ? "" : "s"} failed this week`,
      desc: "Check the SMS history to see why (e.g. an invalid number) and update the client or cleaner's mobile.",
      action: "View SMS history", run: () => navigate("/settings/sms"),
    },
  ].filter(Boolean);

  return (
    <Dialog open={open && issues.length > 0} onOpenChange={setOpen}>
      <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="settings-issues-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-destructive" /> Needs your attention
          </DialogTitle>
          <DialogDescription>Complete the following to clear the alert on Settings.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {issues.map(({ id, icon: Icon, title, desc, action, run }) => (
            <div key={id} className="flex items-start gap-4 rounded-xl border border-destructive/30 bg-destructive/5 p-4" data-testid={`settings-issue-${id}`}>
              <Icon className="w-5 h-5 text-destructive shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-medium text-sm">{title}</div>
                <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                <Button size="sm" onClick={run} data-testid={`settings-issue-${id}-fix`} className="mt-3">{action}</Button>
              </div>
            </div>
          ))}
        </div>
        <button type="button" onClick={() => setOpen(false)} data-testid="settings-issues-later"
          className="text-sm text-muted-foreground hover:text-foreground self-end">Remind me later</button>
      </DialogContent>
    </Dialog>
  );
}
